import { EVENT_NOTE_MAX_LENGTH, type SecurityEventDetail } from '@bartizo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';
import { isApiError } from '../api/client.js';
import { getEvent, updateEvent } from '../api/events.js';
import { listAssignees } from '../api/users.js';
import { useCurrentUser } from '../auth/session-context.js';
import { ActionDialog } from '../components/ActionDialog.js';
import { EventRequests } from '../components/EventRequests.js';
import { ExplanationCard } from '../components/ExplanationCard.js';
import { SuggestCard } from '../components/SuggestCard.js';
import { AddressLink } from '../components/AddressStatus.js';
import { PageHeader } from '../components/PageHeader.js';
import { SeverityBadge } from '../components/StatusBadge.js';
import { effectiveTimezone, formatInstant, formatRelative } from '../lib/time.js';
import { Alert } from '../ui/Alert.js';
import { Button } from '../ui/Button.js';
import { Card } from '../ui/Card.js';
import { SelectField } from '../ui/Field.js';
import { Loading } from '../ui/Skeleton.js';

/**
 * One event, and what an operator does about it (knowledge doc sections 15 and 17).
 *
 * Three things can be changed and nothing else: whether it has been acknowledged,
 * the note beside it, and whose it is. What the agent reported is shown and never
 * edited — an event is a record of what happened, and one that can be rewritten
 * proves nothing later.
 *
 * Claiming is one click ("Assign to me"); assigning to somebody else is a picker
 * over `GET /users/assignees` (P8.4) — the enabled operators and admins, the people
 * who can close an event, and nothing more of the user list.
 *
 * **Accepting a file change is triage, not a server action.** Section 12 lists
 * "accept a file change (update baseline)"; on Wazuh 4.14.8 there is no baseline
 * left to update — a change is reported once and Wazuh takes the file as it now is
 * at the same moment (P7.3). So on a `fim` event, "Accept as expected"
 * acknowledges it with a note saying so, and nothing is sent to the server.
 */

/** What an accepted file change's note starts with, so the reason reads the same everywhere. */
const ACCEPTED_NOTE = 'Accepted as an expected change.';

const acceptedNote = (typed: string): string =>
  (typed.trim() === '' ? ACCEPTED_NOTE : `${ACCEPTED_NOTE} ${typed.trim()}`).slice(
    0,
    EVENT_NOTE_MAX_LENGTH,
  );

export function EventDetailPage(): ReactNode {
  const { id = '' } = useParams();
  const session = useCurrentUser();
  const timezone = effectiveTimezone(session.user.timezone, session.instanceTimezone);
  const canTriage = session.user.role !== 'viewer';

  const queryClient = useQueryClient();
  const event = useQuery({ queryKey: ['event', id], queryFn: () => getEvent(id) });

  if (event.isPending) {
    return (
      <div className="page">
        <Loading />
      </div>
    );
  }

  if (event.isError) {
    return (
      <div className="page">
        <PageHeader title="Event" />
        <Alert tone="danger" title="Could not load that event">
          {isApiError(event.error) && event.error.status === 404
            ? 'There is no event with that id.'
            : 'Try again in a moment.'}{' '}
          <Link to="/events">Back to events</Link>.
        </Alert>
      </div>
    );
  }

  const detail = event.data;
  const refresh = (updated: SecurityEventDetail): void => {
    queryClient.setQueryData(['event', id], updated);
    void queryClient.invalidateQueries({ queryKey: ['events'] });
  };

  return (
    <div className="page">
      <PageHeader
        title={detail.message}
        description={`Reported by ${detail.serverName}`}
        actions={<SeverityBadge severity={detail.severity} />}
      />

      <Card title="What happened">
        <dl className="details">
          <dt>Server</dt>
          <dd>
            <Link to={`/servers/${encodeURIComponent(detail.serverId)}`}>{detail.serverName}</Link>
            {detail.group !== null && <span className="cell-sub">in {detail.group.name}</span>}
          </dd>
          <dt>Category</dt>
          <dd>{detail.category}</dd>
          <dt>Occurred</dt>
          <dd>
            {formatInstant(detail.occurredAt, { timezone })}
            <span className="cell-sub">{formatRelative(detail.occurredAt)}</span>
          </dd>
          <dt>Received</dt>
          <dd>
            {formatInstant(detail.receivedAt, { timezone })}
            {Date.parse(detail.receivedAt) - Date.parse(detail.occurredAt) > 300_000 && (
              <span className="cell-sub">
                Later than it happened, which is a buffered batch arriving after the agent got its
                connection back.
              </span>
            )}
          </dd>
          {detail.sourceIp !== null && (
            <>
              <dt>Source address</dt>
              <dd>
                <AddressLink ip={detail.sourceIp} />
                <span className="cell-sub">
                  Open it to see whether it is stopped on each server.
                </span>
              </dd>
            </>
          )}
          {detail.path !== null && (
            <>
              <dt>Path</dt>
              <dd>
                <code>{detail.path}</code>
              </dd>
            </>
          )}
          {detail.actionTaken !== null && (
            <>
              <dt>Action taken</dt>
              <dd>{detail.actionTaken}</dd>
            </>
          )}
        </dl>
      </Card>

      <EventRequests raw={detail.raw} canMakeRules={canTriage} serverId={detail.serverId} />

      <ExplanationCard eventId={detail.id} canAsk={canTriage} />

      {canTriage && <SuggestCard eventId={detail.id} />}

      <TriageCard detail={detail} canTriage={canTriage} onSaved={refresh} />

      {canTriage && detail.sourceIp !== null && <RespondCard detail={detail} />}

      {detail.raw !== null && detail.raw !== undefined && (
        <Card
          title="Source data"
          description="Exactly what CrowdSec, Wazuh or the module produced, capped in size on the way in."
        >
          <pre className="raw">{JSON.stringify(detail.raw, null, 2)}</pre>
        </Card>
      )}
    </div>
  );
}

function TriageCard({
  detail,
  canTriage,
  onSaved,
}: {
  detail: SecurityEventDetail;
  canTriage: boolean;
  onSaved: (event: SecurityEventDetail) => void;
}): ReactNode {
  const session = useCurrentUser();
  const [note, setNote] = useState(detail.note ?? '');

  const triage = useMutation({
    mutationFn: (patch: Parameters<typeof updateEvent>[1]) => updateEvent(detail.id, patch),
    onSuccess: onSaved,
  });
  const assignees = useQuery({
    queryKey: ['assignees'],
    queryFn: listAssignees,
    enabled: canTriage,
  });
  // Somebody since disabled or made a viewer stays visible as the current value.
  const roster =
    detail.assignee === null ||
    (assignees.data ?? []).some((each) => each.id === detail.assignee?.id)
      ? (assignees.data ?? [])
      : [detail.assignee, ...(assignees.data ?? [])];

  const mine = detail.assignee?.id === session.user.id;
  const noteChanged = note.trim() !== (detail.note ?? '');
  const acceptable = detail.category === 'fim' && !detail.acknowledged;

  return (
    <Card
      title="Triage"
      description={
        canTriage
          ? 'Acknowledging says somebody has looked; the note says what they found.'
          : 'Acknowledging and assigning are an operator’s, so this is read-only for you.'
      }
      aside={
        detail.acknowledged ? (
          <span className="badge badge-success">acknowledged</span>
        ) : (
          <span className="badge badge-accent">open</span>
        )
      }
    >
      <dl className="details">
        <dt>Assigned to</dt>
        <dd>{detail.assignee?.email ?? <span className="muted">nobody</span>}</dd>
      </dl>

      {triage.isError && (
        <Alert tone="danger">
          {triage.error instanceof Error ? triage.error.message : 'Could not save that.'}
        </Alert>
      )}

      {canTriage && (
        <>
          <div className="row">
            <Button
              variant={detail.acknowledged ? 'secondary' : 'primary'}
              busy={triage.isPending}
              onClick={() => {
                triage.mutate({ acknowledged: !detail.acknowledged });
              }}
            >
              {detail.acknowledged ? 'Reopen' : 'Acknowledge'}
            </Button>
            {acceptable && (
              <Button
                busy={triage.isPending}
                onClick={() => {
                  triage.mutate(
                    { acknowledged: true, note: acceptedNote(note) },
                    {
                      onSuccess: (saved) => {
                        setNote(saved.note ?? '');
                      },
                    },
                  );
                }}
              >
                Accept as expected
              </Button>
            )}
            <Button
              busy={triage.isPending}
              onClick={() => {
                triage.mutate({ assignedTo: mine ? null : session.user.id });
              }}
            >
              {mine ? 'Release' : 'Assign to me'}
            </Button>
          </div>
          <SelectField
            label="Assigned to"
            value={detail.assignee?.id ?? ''}
            disabled={triage.isPending || assignees.isPending}
            onChange={(event) => {
              triage.mutate({ assignedTo: event.target.value === '' ? null : event.target.value });
            }}
          >
            <option value="">Nobody</option>
            {roster.map((person) => (
              <option key={person.id} value={person.id}>
                {person.email}
              </option>
            ))}
          </SelectField>
          {acceptable && (
            <p className="field-hint">
              Wazuh has already taken the file as it is now for its baseline, so accepting changes
              nothing on the server: it acknowledges the event and notes that the change was
              expected, with anything written below. If it was not expected, put the file right;
              Wazuh reports that change too.
            </p>
          )}

          <div className="field">
            <label htmlFor="event-note">Note</label>
            <span className="field-hint" id="event-note-hint">
              What was found, and what was done. Kept out of the audit log, which records only that
              a note exists.
            </span>
            <textarea
              id="event-note"
              aria-describedby="event-note-hint"
              rows={4}
              maxLength={EVENT_NOTE_MAX_LENGTH}
              value={note}
              onChange={(changed) => {
                setNote(changed.target.value);
              }}
            />
          </div>

          <div className="row">
            <Button
              disabled={!noteChanged}
              busy={triage.isPending}
              onClick={() => {
                triage.mutate({ note: note.trim() });
              }}
            >
              Save note
            </Button>
            {/* Scoped to a note save by what the last request actually carried.
                Keyed on `isSuccess` alone it said "Saved." beside an untouched note
                the moment somebody acknowledged the event — found by clicking it. */}
            {triage.isSuccess && triage.variables.note !== undefined && !noteChanged && (
              <span className="muted text-sm">Saved.</span>
            )}
          </div>
        </>
      )}

      {!canTriage && detail.note !== null && <p className="text-sm">{detail.note}</p>}
    </Card>
  );
}

/**
 * Section 12 from the event that prompted it: the source address is already
 * filled in, and the target starts at the server that reported it. The dialog is
 * the same one the server page opens, preview and all.
 */
function RespondCard({ detail }: { detail: SecurityEventDetail }): ReactNode {
  const [dialog, setDialog] = useState<'ban_ip' | 'unban_ip' | null>(null);
  const sourceIp = detail.sourceIp ?? '';

  return (
    <Card
      title="Respond"
      description={`Ban or unban ${sourceIp} through CrowdSec, on this server or more.`}
    >
      <div className="row">
        <Button
          variant="danger"
          onClick={() => {
            setDialog('ban_ip');
          }}
        >
          Ban this address
        </Button>
        <Button
          onClick={() => {
            setDialog('unban_ip');
          }}
        >
          Unban it
        </Button>
      </div>
      {dialog !== null && (
        <ActionDialog
          type={dialog}
          server={{ id: detail.serverId, name: detail.serverName, group: detail.group }}
          initialIp={sourceIp}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </Card>
  );
}
